import React, { useState, useEffect, useMemo, useRef } from 'react';
import { getAuth, updateEmail, sendEmailVerification, RecaptchaVerifier, signInWithPhoneNumber, linkWithPhoneNumber, linkWithCredential, sendPasswordResetEmail, updatePassword, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';
import { db } from '../firebase.config';
import { doc, getDoc, setDoc, serverTimestamp, updateDoc } from '../firebase.config';
import { checkPhoneExists, normalizePhoneNumber, isPhone } from '../utils/phoneValidation';
import { getSettingsDoc } from '../utils/appArtifacts';
import { useNotification } from '../contexts/NotificationContext';

export default function LoginSettings({ userId, appId }) {
  const auth = getAuth();
  const { success, error, warning, info } = useNotification();
  const [email, setEmail] = useState('');
  const [currentEmail, setCurrentEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [recoveryPhones, setRecoveryPhones] = useState('');
  const [otp, setOtp] = useState('');
  const [msg, setMsg] = useState('');
  const [loading, setLoading] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const recaptchaRef = useRef(null);
  
  // MPIN states
  const [mpin, setMpin] = useState('');
  const [confirmMpin, setConfirmMpin] = useState('');
  const [mpinError, setMpinError] = useState('');
  const [mpinSuccess, setMpinSuccess] = useState('');
  const [isMpinSet, setIsMpinSet] = useState(false);
  
  // Password states
  const [pwdMsg, setPwdMsg] = useState('');
  const [pwdError, setPwdError] = useState('');
  const [showChangePwd, setShowChangePwd] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [linkPassword, setLinkPassword] = useState('');
  const [confirmLinkPassword, setConfirmLinkPassword] = useState('');

  const isPhoneOnlyAccount = useMemo(() => {
    const user = auth.currentUser;
    if (!user) return false;
    const hasPhoneProvider = user.providerData?.some((p) => p.providerId === 'phone');
    return hasPhoneProvider && !user.email;
  }, [currentEmail, auth.currentUser?.email, auth.currentUser?.providerData]);

  useEffect(() => {
    (async () => {
      try {
        const user = auth.currentUser;
        if (user) {
          setCurrentEmail(user.email || '');
          setEmail(user.email || '');
        }
        if (userId && db) {
          // Try new path first, fallback to legacy
          try {
            const newRef = doc(db, 'users', userId);
            const newSnap = await getDoc(newRef);
            if (newSnap.exists()) {
              const data = newSnap.data();
              setPhone(data.phoneNumber || '');
              setRecoveryPhones((data.recoveryPhones || []).join(', '));
              
              // Check if MPIN is set in user profile
              if (data.mpin || data.login_mpin_hash) {
                setIsMpinSet(true);
              }
            } else if (appId) {
              // Fallback to legacy path
              const legacyRef = doc(db, `artifacts/${appId}/users/${userId}`);
              const legacySnap = await getDoc(legacyRef);
              const data = legacySnap.exists() ? legacySnap.data() : {};
              setPhone(data.phoneNumber || '');
              setRecoveryPhones((data.recoveryPhones || []).join(', '));
            }

            // MPIN and password are already loaded from users collection above
            console.log('✅ User data loaded from users collection');
          } catch (e) {
            console.log('LoginSettings: Error loading user data:', e);
          }
        }
      } catch (_) {}
    })();
  }, [auth, userId, appId]);

  // Load MPIN status
  useEffect(() => {
    const loadMpinStatus = async () => {
      if (!userId) return;
      
      // First check local storage (for offline mode)
      try {
        const localMpin = localStorage.getItem('user_mpin');
        if (localMpin) {
          const mpinData = JSON.parse(localMpin);
          if (mpinData.userId === userId) {
            setIsMpinSet(!!mpinData.mpin);
            return; // Use local data if available
          }
        }
      } catch (error) {
        console.warn('Error reading local MPIN:', error);
      }
      
      // Fallback to database
      try {
        const settingsRef = getSettingsDoc(userId);
        const settingsSnap = await getDoc(settingsRef);
        if (settingsSnap.exists()) {
          const data = settingsSnap.data();
          setIsMpinSet(!!data.mpin);
          
          // Sync to local storage if found in database
          if (data.mpin) {
            const mpinData = {
              mpin: data.mpin,
              mpinSetAt: data.mpinSetAt,
              userId: userId
            };
            localStorage.setItem('user_mpin', JSON.stringify(mpinData));
          }
        }
      } catch (error) {
        console.error('Error loading MPIN status from database:', error);
      }
    };
    loadMpinStatus();
  }, [userId]);

  // Sync pending MPIN changes when online
  useEffect(() => {
    const syncPendingMpin = async () => {
      try {
        const pendingSync = localStorage.getItem('pending_mpin_sync');
        if (pendingSync && userId) {
          const mpinData = JSON.parse(pendingSync);
          if (mpinData.userId === userId) {
            const settingsRef = getSettingsDoc(userId);
            const userDoc = await getDoc(settingsRef);
            if (userDoc.exists()) {
              await updateDoc(settingsRef, mpinData);
            } else {
              await setDoc(settingsRef, mpinData);
            }
            // Remove pending sync after successful sync
            localStorage.removeItem('pending_mpin_sync');
            console.log('Pending MPIN sync completed');
          }
        }
      } catch (error) {
        console.warn('Failed to sync pending MPIN:', error);
      }
    };

    // Check for pending sync on component mount
    syncPendingMpin();
    
    // Also listen for online events to sync when connection is restored
    const handleOnline = () => {
      syncPendingMpin();
    };
    
    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, [userId]);

  const ensureRecaptcha = () => {
    if (recaptchaRef.current) return recaptchaRef.current;
    
    // Check if the recaptcha container exists
    const container = document.getElementById('login-settings-recaptcha');
    if (!container) {
      console.warn('reCAPTCHA container not found, creating one');
      const div = document.createElement('div');
      div.id = 'login-settings-recaptcha';
      div.style.display = 'none';
      document.body.appendChild(div);
    }
    
    try {
      recaptchaRef.current = new RecaptchaVerifier(auth, 'login-settings-recaptcha', { 
        size: 'invisible',
        callback: () => {
          console.log('reCAPTCHA solved');
        },
        'expired-callback': () => {
          console.log('reCAPTCHA expired');
        }
      });
      return recaptchaRef.current;
    } catch (e) {
      console.error('Error creating reCAPTCHA:', e);
      return null;
    }
  };

  const saveEmail = async () => {
    try {
      setMsg('');
      setLoading(true);
      const user = auth.currentUser;
      if (!user) {
        setMsg('Please sign in again.');
        return;
      }

      if (!email?.trim()) {
        warning('Enter a valid email address.', { title: 'Validation Error' });
        setMsg('Enter a valid email address.');
        return;
      }

      if (isPhoneOnlyAccount) {
        if (!linkPassword || linkPassword.length < 6) {
          warning('Choose a password (at least 6 characters) for your new email login.', {
            title: 'Password Required',
          });
          setMsg('Password must be at least 6 characters.');
          return;
        }
        if (linkPassword !== confirmLinkPassword) {
          warning('Passwords do not match.', { title: 'Validation Error' });
          setMsg('Passwords do not match.');
          return;
        }

        const normalizedEmail = email.trim().toLowerCase();
        const cred = EmailAuthProvider.credential(normalizedEmail, linkPassword);
        await linkWithCredential(user, cred);
        await user.reload();
        try {
          await sendEmailVerification(auth.currentUser);
        } catch (_) {}
      } else {
        if (email === currentEmail) {
          info('No changes to email.', { title: 'No Changes', duration: 3000 });
          setMsg('No changes to email.');
          return;
        }
        await updateEmail(user, email.trim().toLowerCase());
        try {
          await sendEmailVerification(auth.currentUser);
        } catch (_) {}
      }

      const linkedEmail = auth.currentUser?.email || email.trim().toLowerCase();
      try {
        if (db && userId) {
          const usersRef = doc(db, 'users', userId);
          await setDoc(
            usersRef,
            {
              email: linkedEmail,
              emailVerified: !!auth.currentUser?.emailVerified,
              updatedAt: serverTimestamp(),
            },
            { merge: true }
          );
          if (appId) {
            const artifactsUserRef = doc(db, `artifacts/${appId}/users/${userId}`);
            await setDoc(artifactsUserRef, { email: linkedEmail, updatedAt: serverTimestamp() }, { merge: true });
          }
          const phoneE164 = auth.currentUser?.phoneNumber;
          if (phoneE164 && linkedEmail) {
            await setDoc(
              doc(db, 'phone_index', phoneE164),
              { uid: userId, email: linkedEmail, updatedAt: serverTimestamp() },
              { merge: true }
            );
          }
        }
      } catch (_) {}

      setCurrentEmail(linkedEmail);
      setEmail(linkedEmail);
      setLinkPassword('');
      setConfirmLinkPassword('');
      try {
        sessionStorage.removeItem('acctoo_prompt_link_email');
      } catch (_) {}

      success(
        isPhoneOnlyAccount
          ? 'Email linked. Check your inbox for the verification link.'
          : 'Email updated. Verification link sent.',
        { title: 'Email Updated', duration: 6000 }
      );
      setMsg(
        isPhoneOnlyAccount
          ? 'Email linked. Open the verification link in your inbox, then return here.'
          : 'Email updated. Verification link sent.'
      );
    } catch (e) {
      const code = e?.code || '';
      let txt = e?.message || 'Failed to update email';
      if (code === 'auth/email-already-in-use') {
        txt = 'That email is already used by another account. Sign in with that email or use a different address.';
      } else if (code === 'auth/requires-recent-login') {
        txt = 'For security, sign out and sign in again with your phone OTP, then retry linking email.';
      } else if (code === 'auth/weak-password') {
        txt = 'Password is too weak. Use a stronger password (uppercase, lowercase, number).';
      }
      error(txt, { title: 'Update Failed', duration: 7000 });
      setMsg(txt);
    } finally {
      setLoading(false);
    }
  };

  const sendPhoneOtp = async () => {
    try {
      setMsg(''); setLoading(true);
      if (!isPhone(phone)) { warning('Enter a valid phone number (+91...)', { title: 'Validation Error' }); setMsg('Enter a valid phone number (+91...)'); setLoading(false); return; }
      
      // Check if phone number already exists
      const phoneCheck = await checkPhoneExists(normalizePhoneNumber(phone), userId);
      if (phoneCheck.exists) {
        error(phoneCheck.message, { title: 'Phone Already Exists', duration: 7000 });
        setMsg(`❌ ${phoneCheck.message}`);
        setLoading(false);
        return;
      }
      
      // Development bypass ONLY in development environment
      // SECURITY: This bypass is disabled in production builds
      if (process.env.NODE_ENV === 'development' && 
          (window.location.hostname === 'localhost' || window.location.hostname.includes('127.0.0.1'))) {
        // Skip all Firebase auth, just set a mock confirmation
        setConfirm({ 
          confirm: async (code) => {
            if (code === '123456') {
              return { user: auth.currentUser };
            } else {
              throw new Error('Invalid OTP. Use 123456 for testing.');
            }
          }
        });
        info('OTP sent (Development mode - use 123456)', { title: 'OTP Sent', duration: 5000 });
        setMsg('✅ OTP sent (Development mode - use 123456)');
        setLoading(false);
        return;
      }
      
      // Production flow - check uniqueness BEFORE sending OTP to save Firebase costs
      const productionPhoneCheck = await checkPhoneExists(normalizePhoneNumber(phone), userId);
      if (productionPhoneCheck.exists) {
        error(productionPhoneCheck.message, { title: 'Phone Already Exists', duration: 7000 });
        setMsg(`❌ ${productionPhoneCheck.message}`);
        setLoading(false);
        return;
      }
      
      const target = auth.currentUser;
      const appVerifier = ensureRecaptcha();
      if (!appVerifier) {
        error('reCAPTCHA initialization failed. Please refresh the page.', { title: 'reCAPTCHA Error', duration: 7000 });
        setMsg('reCAPTCHA initialization failed. Please refresh the page.');
        return;
      }
      
      const phoneNumber = phone.startsWith('+') ? phone : `+91${phone}`;
      let conf = null;
      try {
        conf = await linkWithPhoneNumber(target, phoneNumber, appVerifier);
      } catch (e) {
        // Fallback to signInWithPhoneNumber if linking isn't allowed
        conf = await signInWithPhoneNumber(auth, phoneNumber, appVerifier);
      }
      setConfirm(conf);
      success('OTP sent', { title: 'OTP Sent', duration: 5000 });
      setMsg('OTP sent');
    } catch (e) {
      const txt = String(e?.message || '').includes('invalid-app-credential')
        ? 'Phone OTP failed: Please ensure this domain is authorized in Firebase Auth (Authorized domains) and reCAPTCHA is enabled.'
        : (e?.message || 'Failed to send OTP');
      error(txt, { title: 'OTP Failed', duration: 7000 });
      setMsg(txt);
    } finally { setLoading(false); }
  };

  const verifyPhoneOtp = async () => {
    try {
      setMsg(''); setLoading(true);
      if (!confirm) { warning('Please send OTP first', { title: 'Validation Error' }); setMsg('Please send OTP first'); return; }
      await confirm.confirm(otp);
      
      // Define e164 for phone index update
      const primary = (phone || '').trim();
      const e164 = primary.startsWith('+') ? primary : `+91${primary}`;
      
      // Save primary phone and recovery phones in user profile (new path)
      const usersRef = doc(db, 'users', userId);
      await setDoc(usersRef, {
        phoneNumber: phone,
        recoveryPhones: (recoveryPhones || '').split(',').map(s=>s.trim()).filter(Boolean),
        updatedAt: serverTimestamp()
      }, { merge: true });
      
      // Also mirror to legacy artifacts path if appId exists (for backward compatibility)
      if (appId) {
        try {
          const artifactsUserRef = doc(db, `artifacts/${appId}/users/${userId}`);
          await setDoc(artifactsUserRef, {
            phoneNumber: phone,
            recoveryPhones: (recoveryPhones || '').split(',').map(s=>s.trim()).filter(Boolean),
            updatedAt: serverTimestamp()
          }, { merge: true });
        } catch (legacyError) {
          console.log('LoginSettings: Legacy mirror failed (non-critical):', legacyError);
        }
      }
      // Write phone index for public phone->email resolution (PRIMARY only)
      try {
        const email = (auth.currentUser?.email) || '';
        if (e164 && email) {
          await setDoc(doc(db, 'phone_index', e164), { uid: userId, email, updatedAt: serverTimestamp() }, { merge: true });
          console.log('LoginSettings: Phone index updated successfully');
        }
      } catch (indexError) {
        console.log('LoginSettings: Phone index update failed (non-critical):', indexError);
        // Don't fail the whole operation if phone index fails
      }
      success('Primary phone verified and saved', { title: 'Phone Verified', duration: 6000 });
      setMsg('Primary phone verified and saved');
    } catch (e) {
      error(e?.message || 'OTP verification failed', { title: 'Verification Failed', duration: 7000 });
      setMsg(e?.message || 'OTP verification failed');
    } finally { setLoading(false); }
  };

  const saveRecovery = async () => {
    try {
      setMsg(''); setLoading(true);
      const phones = (recoveryPhones || '').split(',').map(s=>s.trim()).filter(Boolean);
      
      // Check uniqueness for each recovery phone
      for (const phoneNum of phones) {
        if (isPhone(phoneNum)) {
          const phoneCheck = await checkPhoneExists(normalizePhoneNumber(phoneNum), userId);
          if (phoneCheck.exists) {
            error(`Recovery phone ${phoneNum} is already registered with another account`, { title: 'Phone Already Exists', duration: 7000 });
            setMsg(`❌ Recovery phone ${phoneNum} is already registered with another account`);
            setLoading(false);
            return;
          }
        }
      }
      
      // Save to new user path
      const usersRef = doc(db, 'users', userId);
      await setDoc(usersRef, { recoveryPhones: phones, updatedAt: serverTimestamp() }, { merge: true });
      
      // Mirror to legacy path (with error handling)
      if (appId) {
        try {
          const artifactsUserRef = doc(db, `artifacts/${appId}/users/${userId}`);
          await setDoc(artifactsUserRef, { recoveryPhones: phones, updatedAt: serverTimestamp() }, { merge: true });
        } catch (legacyError) {
          console.log('LoginSettings: Legacy recovery save failed (non-critical):', legacyError);
        }
      }
      
      success('Recovery phones saved', { title: 'Recovery Phones Saved', duration: 6000 });
      setMsg('✅ Recovery phones saved');
    } catch (e) {
      console.error('LoginSettings: Recovery save error:', e);
      error(e?.message || 'Failed to save recovery phones', { title: 'Save Failed', duration: 7000 });
      setMsg(e?.message || 'Failed to save recovery phones');
    } finally { setLoading(false); }
  };

  // MPIN handling functions
  const handleMpinSubmit = async (e) => {
    e.preventDefault();
    setMpinError('');
    setMpinSuccess('');

    if (mpin.length !== 4) {
      setMpinError('MPIN must be exactly 4 digits');
      return;
    }

    if (mpin !== confirmMpin) {
      setMpinError('MPIN and confirmation do not match');
      return;
    }

    if (!/^\d{4}$/.test(mpin)) {
      setMpinError('MPIN must contain only numbers');
      return;
    }

    try {
      const normalized = mpin.trim();
      const hashedMpin = btoa(normalized); // demo only (replace with proper KDF in production)
      const mpinData = {
        mpin: hashedMpin,
        mpinSetAt: new Date(),
        userId: userId
      };

      // Store in local storage first (for offline access)
      localStorage.setItem('user_mpin', JSON.stringify(mpinData));
      
      // Store in database (with error handling for offline mode)
      try {
        const settingsRef = getSettingsDoc(userId);
        const userDoc = await getDoc(settingsRef);
        if (userDoc.exists()) {
          await updateDoc(settingsRef, mpinData);
        } else {
          await setDoc(settingsRef, mpinData);
        }
        console.log('MPIN saved to database successfully');
      } catch (dbError) {
        console.warn('Failed to save MPIN to database (offline mode):', dbError);
        // Store pending sync in local storage
        localStorage.setItem('pending_mpin_sync', JSON.stringify(mpinData));
      }
      
      setMpinSuccess('MPIN set successfully!');
      setMpin('');
      setConfirmMpin('');
      setIsMpinSet(true);
      setTimeout(() => setMpinSuccess(''), 3000);
    } catch (error) {
      console.error('Error setting MPIN:', error);
      setMpinError('Failed to set MPIN. Please try again.');
    }
  };

  // Password handling functions
  const handleForgotPassword = async () => {
    try {
      setPwdMsg(''); setPwdError('');
      const email = auth.currentUser?.email;
      if (!email) {
        setPwdError('No email address found');
        return;
      }
      
      const actionCodeSettings = {
        url: `${window.location.origin}/login`,
        handleCodeInApp: false,
      };
      
      await sendPasswordResetEmail(auth, email, actionCodeSettings);
      setPwdMsg('Password reset link sent to your email.');
    } catch (e) {
      setPwdError(e.message || 'Failed to send reset email');
    }
  };

  const handleChangePasswordSubmit = async (e) => {
    e.preventDefault();
    setPwdMsg(''); setPwdError('');
    try {
      if (!newPassword || newPassword.length < 6) throw new Error('New password must be at least 6 characters');
      if (newPassword !== confirmNewPassword) throw new Error('Passwords do not match');
      const user = auth.currentUser;
      if (!user || !user.email) throw new Error('User not authenticated');
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, newPassword);
      setPwdMsg('Password updated successfully.');
      setShowChangePwd(false);
      setCurrentPassword(''); setNewPassword(''); setConfirmNewPassword('');
    } catch (e) {
      // Check for specific authentication errors
      if (e.code === 'auth/wrong-password' || e.code === 'auth/invalid-credential') {
        setPwdError('Incorrect password');
      } else if (e.code === 'auth/too-many-requests') {
        setPwdError('Too many failed attempts. Please try again later.');
      } else if (e.code === 'auth/weak-password') {
        setPwdError('Password is too weak. Please choose a stronger password.');
      } else {
        setPwdError(e.message || 'Failed to update password');
      }
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-4 sm:p-6 bg-white rounded-lg shadow">
      <h2 className="text-xl sm:text-2xl font-bold mb-4">Login & Security</h2>

      {msg && <div className="mb-4 p-3 rounded text-sm bg-blue-50 text-blue-700">{msg}</div>}

      <div className="space-y-6">
        <div className="border rounded p-3 sm:p-4">
          <h3 className="font-semibold mb-2 text-sm sm:text-base">
            {isPhoneOnlyAccount ? 'Add email & password' : 'Primary Email'}
          </h3>
          {isPhoneOnlyAccount && (
            <p className="text-xs text-blue-800 bg-blue-50 border border-blue-100 rounded-lg p-2.5 mb-3">
              Your account was created with phone OTP. Add an email and password here for recovery, reports, and
              password login. We will send a verification link after you save.
            </p>
          )}
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              className="flex-1 border rounded px-3 py-2 text-sm"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
            />
            {!isPhoneOnlyAccount && (
              <button disabled={loading} onClick={saveEmail} className="px-4 py-2 bg-blue-600 text-white rounded text-sm whitespace-nowrap">
                Save
              </button>
            )}
          </div>
          {isPhoneOnlyAccount && (
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
              <input
                type="password"
                className="border rounded px-3 py-2 text-sm"
                placeholder="Create password"
                value={linkPassword}
                onChange={(e) => setLinkPassword(e.target.value)}
                autoComplete="new-password"
              />
              <input
                type="password"
                className="border rounded px-3 py-2 text-sm"
                placeholder="Confirm password"
                value={confirmLinkPassword}
                onChange={(e) => setConfirmLinkPassword(e.target.value)}
                autoComplete="new-password"
              />
            </div>
          )}
          {isPhoneOnlyAccount ? (
            <button
              disabled={loading}
              onClick={saveEmail}
              className="mt-3 w-full sm:w-auto px-4 py-2.5 bg-blue-600 text-white rounded text-sm font-semibold"
            >
              Link email & send verification
            </button>
          ) : (
            <p className="text-xs text-gray-500 mt-1">We will send a verification link when you change email.</p>
          )}
        </div>

        {!isPhoneOnlyAccount && (
        <div className="border rounded p-3 sm:p-4">
          <h3 className="font-semibold mb-2 text-sm sm:text-base">Password</h3>
          <div className="flex flex-col sm:flex-row gap-2 mb-3">
            <button onClick={() => setShowChangePwd(!showChangePwd)} className="px-3 py-1.5 text-sm rounded bg-gray-100 hover:bg-gray-200">Change Password</button>
            <button onClick={handleForgotPassword} className="px-3 py-1.5 text-sm rounded bg-gray-100 hover:bg-gray-200">Forgot Password</button>
          </div>
          {showChangePwd && (
            <form onSubmit={handleChangePasswordSubmit} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              <input type="password" className="px-3 py-2 border rounded text-sm" placeholder="Current password" value={currentPassword} onChange={e=>setCurrentPassword(e.target.value)} />
              <input type="password" className="px-3 py-2 border rounded text-sm" placeholder="New password" value={newPassword} onChange={e=>setNewPassword(e.target.value)} />
              <input type="password" className="px-3 py-2 border rounded text-sm" placeholder="Confirm new password" value={confirmNewPassword} onChange={e=>setConfirmNewPassword(e.target.value)} />
              <div className="sm:col-span-2 lg:col-span-3 flex flex-col sm:flex-row gap-2">
                <button type="submit" className="px-3 py-1.5 text-sm rounded bg-blue-600 text-white hover:bg-blue-700">Update</button>
                <button type="button" onClick={() => { setShowChangePwd(false); setCurrentPassword(''); setNewPassword(''); setConfirmNewPassword(''); }} className="px-3 py-1.5 text-sm rounded bg-gray-100 hover:bg-gray-200">Cancel</button>
              </div>
            </form>
          )}
          {(pwdMsg || pwdError) && (
            <div className={`text-sm mt-2 ${pwdError ? 'text-red-600' : 'text-green-600'}`}>{pwdError || pwdMsg}</div>
          )}
        </div>
        )}

        <div className="border rounded p-3 sm:p-4">
          <h3 className="font-semibold mb-2 text-sm sm:text-base">Primary Mobile</h3>
          <div className="flex flex-col sm:flex-row gap-2 mb-2">
            <input className="flex-1 border rounded px-3 py-2 text-sm" type="text" value={phone} onChange={e=>setPhone(e.target.value)} placeholder="+91XXXXXXXXXX" />
            <button disabled={loading} onClick={sendPhoneOtp} className="px-4 py-2 bg-blue-600 text-white rounded text-sm whitespace-nowrap">Send OTP</button>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <input className="flex-1 border rounded px-3 py-2 text-sm" type="text" value={otp} onChange={e=>setOtp(e.target.value)} placeholder="Enter OTP" />
            <button disabled={loading} onClick={verifyPhoneOtp} className="px-4 py-2 bg-green-600 text-white rounded text-sm whitespace-nowrap">Verify & Save</button>
          </div>
          <div id="login-settings-recaptcha" />
        </div>

        <div className="border rounded p-3 sm:p-4">
          <h3 className="font-semibold mb-2 text-sm sm:text-base">Recovery Mobile Numbers</h3>
          <div className="flex flex-col sm:flex-row gap-2">
            <input className="flex-1 border rounded px-3 py-2 text-sm" type="text" value={recoveryPhones} onChange={e=>setRecoveryPhones(e.target.value)} placeholder="Comma-separated list" />
            <button disabled={loading} onClick={saveRecovery} className="px-4 py-2 bg-blue-600 text-white rounded text-sm whitespace-nowrap">Save</button>
          </div>
          <p className="text-xs text-gray-500 mt-1">We will use these numbers for account recovery and alerts.</p>
        </div>

        <div className="border rounded p-3 sm:p-4">
          <h3 className="font-semibold mb-2 text-sm sm:text-base">MPIN (Mobile PIN)</h3>
          <p className="text-xs sm:text-sm text-gray-600 mb-4">
            Set a 4-digit MPIN for phone / recovery flows. Page-level MPIN locks are currently turned off across the app (web and mobile); you can still store an MPIN here for future use.
          </p>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-xs sm:text-sm text-amber-900">
            Note: MPIN is stored with reversible encoding today (not a strong hash). Treat it as a convenience PIN, not bank-grade security.
          </div> 
          {isMpinSet && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-green-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-green-800 font-medium">MPIN is already set</span>
              </div>
            </div>
          )}

          <form onSubmit={handleMpinSubmit} className="space-y-4">
            <div>
              <label htmlFor="mpin" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                Enter 4-digit MPIN
              </label>
              <input
                type="password"
                id="mpin"
                value={mpin}
                onChange={(e) => setMpin(e.target.value)}
                maxLength={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                placeholder="Enter 4 digits"
              />
            </div>

            <div>
              <label htmlFor="confirmMpin" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                Confirm MPIN
              </label>
              <input
                type="password"
                id="confirmMpin"
                value={confirmMpin}
                onChange={(e) => setConfirmMpin(e.target.value)}
                maxLength={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                placeholder="Confirm 4 digits"
              />
            </div>

            {mpinError && (
              <div className="text-red-600 text-xs sm:text-sm">{mpinError}</div>
            )}

            {mpinSuccess && (
              <div className="text-green-600 text-xs sm:text-sm">{mpinSuccess}</div>
            )}

            <button 
              type="submit" 
              className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors text-sm"
            >
              {isMpinSet ? 'Update MPIN' : 'Set MPIN'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
